import React, { useContext, useEffect, useState } from 'react';
import { Tab, Table, Tabs } from 'react-bootstrap';
import IssuesTab from './issues/IssuesTab';
import AcceptedOrderLines from './accepted/AcceptedOrderLines';
import LoadingSpinner from '../../common/LoadingSpinner';
import OrderLines from './lines/OrderLines';
import OrderContext from './OrderContext';
import OrderLineRow from './lines/OrderLineRow';
import axios from 'axios';
import UserContext from '../../user/UserContext';
import AuthContext from '../../authentication/AuthContext';
import { useAlert } from 'react-alert';

export default function OrderTabs({
    loading,
    orderLinesLoading,
    searchResults,
    searchResultCount,
    searched
}) {
    const { orderHeader, basket, orderId, tabKey, setTabKey } = useContext(OrderContext);
    const { userDetails } = useContext(UserContext);
    const cas = useContext(AuthContext);
    const alert = useAlert();

    const [upsells, setUpsells] = useState([]);
    const [searchResultRows, setSearchResultRows] = useState([]);

    const [bestsellersLoading, setBestsellersLoading] = useState(false);
    const [bestsellingProducts, setBestsellingProducts] = useState([]);

    const [recentLoading, setRecentLoading] = useState(false);
    const [recentProducts, setRecentProducts] = useState([]);

    // Only load bestsellers and recents on page load to avoid recent unnecessary load and recent items vanishing!
    //   We need these state variables as we require the order header to be available before we can load bestsellers
    // and recents (otherwise we'd use a useEffect hook with an empty dependency array).
    const [shouldLoadBestsellers, setShouldLoadBestsellers] = useState(true);
    const [shouldLoadRecents, setShouldLoadRecents] = useState(true);

    let retrieveData = (url, responseCallback, errorMessage) => {
        axios
            .get(url, {
                headers: {
                    Authorization: cas,
                    'Content-Type': 'application/json'
                }
            })
            .then(responseCallback)
            .catch(() => {
                alert.error(errorMessage);
            });
    };

    let getUpsells = () => {
        retrieveData(
            window.CONFIG['ws.activoc-web-order-api.upsell.products'].replace('{id}', orderId),
            response => {
                setUpsells(response.data);
            },
            'ERROR: Unable to retrieve upsell offers.'
        );
    };

    useEffect(() => {
        if (
            orderHeader &&
            shouldLoadBestsellers &&
            userDetails.currentFranchise &&
            orderHeader.orderStatus === 'OPEN'
        ) {
            setBestsellersLoading(true);
            retrieveData(
                window.CONFIG['ws.activoc-web-order-api.products.bestselling']
                    .replace('{franchiseCode}', userDetails.currentFranchise.code)
                    .replace('{supplierCode}', orderHeader.supplierCode),
                response => {
                    setBestsellingProducts(response.data);
                    setBestsellersLoading(false);
                    setShouldLoadBestsellers(false);
                },
                'ERROR: Unable to retrieve bestsellers.'
            );
        }
        if (orderHeader && shouldLoadRecents && orderHeader.orderStatus === 'OPEN') {
            setRecentLoading(true);
            retrieveData(
                window.CONFIG['ws.activoc-web-order-api.products.recent'].replace('{orderId}', orderId),
                response => {
                    setRecentProducts(response.data.items);
                    setRecentLoading(false);
                    setShouldLoadRecents(false);
                },
                'ERROR: Unable to retrieve recently ordered products.'
            );
        }
        // eslint-disable-next-line
    }, [orderHeader, userDetails]);

    useEffect(
        () => getUpsells(),
        // eslint-disable-next-line
        [cas, orderId]
    );

    useEffect(() => {
        setSearchResultRows(
            searchResults.length > 0
                ? searchResults.map(product => (
                      <OrderLineRow
                          product={product}
                          code={product.supplierProductCode}
                          key={product.supplierProductCode}
                          orderLine={
                              basket.items.filter(line => line.productCode === product.supplierProductCode)[0]
                          }
                          showAvailability={true}
                      />
                  ))
                : [
                      <tr className={'order-search-placeholder'} key={1}>
                          <td colSpan={'100%'}>Your search returned no results.</td>
                      </tr>
                  ]
        );
        // eslint-disable-next-line
    }, [searchResults, basket, cas, orderId]);

    return (
        <Tabs id={'order-lines-nav'} activeKey={tabKey} onSelect={k => setTabKey(k)}>
            {orderHeader &&
                (basket.rejected.length > 0 ||
                    basket.alternatives.length > 0 ||
                    basket.presell.length > 0 ||
                    basket.substitutes.length > 0 ||
                    basket.contentLines.length > 0 ||
                    basket.capped.length > 0) && (
                    <Tab
                        eventKey={'issues'}
                        title={
                            <>
                                <span>Issues</span>
                                {orderHeader.orderStatus === 'OPEN' && (
                                    <span className={'issues-badge'}>
                                        {basket.rejected.length +
                                            basket.alternatives.length +
                                            basket.presell.length +
                                            basket.substitutes.length +
                                            basket.contentLines.length +
                                            basket.capped.length}
                                    </span>
                                )}
                            </>
                        }
                    >
                        <IssuesTab loading={orderLinesLoading} />
                    </Tab>
                )}
            {orderHeader && (
                <Tab eventKey={'accepted'} title={'Accepted'}>
                    <AcceptedOrderLines
                        orderLines={basket.items}
                        loading={orderLinesLoading}
                        showDeleteIcons={true}
                    />
                </Tab>
            )}
            {orderHeader && orderHeader.orderStatus === 'OPEN' && (
                <Tab
                    eventKey={'search'}
                    title={searched ? 'Search Results (' + searchResultCount + ')' : 'Search Results (0)'}
                >
                    <Table className={'product-range-table'} data-testid={'search-table'}>
                        <thead>
                            <tr>
                                <th className={'image-column column-7'}>Image</th>
                                <th className={'column-7'}>Code</th>
                                <th>Description</th>
                                <th className={'column-9'}>Unit Size</th>
                                {userDetails.currentFranchise && userDetails.currentFranchise.rebates && (
                                    <th className={'column-9'}>Best Cost</th>
                                )}
                                <th className={'column-9'}>Invoice Cost</th>
                                <th className={'column-7'}>RSP</th>
                                <th className={'column-7'}>Margin</th>
                                {userDetails?.currentFranchise?.controlGroups?.includes(
                                    'Availability Control Group'
                                ) && <th className={'column-7'}>Availability</th>}
                                <th className={'column-7 centered'}>Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={'100%'}>
                                        <LoadingSpinner />
                                    </td>
                                </tr>
                            ) : searched ? (
                                searchResultRows
                            ) : (
                                [
                                    <tr className={'order-search-placeholder'} key={1}>
                                        <td colSpan={'100%'}>Enter search criteria.</td>
                                    </tr>
                                ]
                            )}
                        </tbody>
                    </Table>
                </Tab>
            )}
            {orderHeader && orderHeader.orderStatus === 'OPEN' && (
                <Tab eventKey={'bestsellers'} title={'Bestsellers'}>
                    <OrderLines
                        products={bestsellingProducts}
                        loading={bestsellersLoading}
                        imageUrl={'/banners/banner-bestsellers.jpg'}
                        imageAlt={'Bestsellers Banner'}
                        upsoldFrom={'Bestseller Search'}
                    />
                </Tab>
            )}
            {orderHeader && orderHeader.orderStatus === 'OPEN' && (
                <Tab
                    eventKey={'recent'}
                    title={
                        <span>
                            <span className={'new-tab-title'}>New</span>Recent
                        </span>
                    }
                    tabClassName={'new-tab'}
                >
                    <OrderLines
                        products={recentProducts}
                        loading={recentLoading}
                        imageUrl={'/banners/banner-recentbasket.jpg'}
                        imageAlt={'Recently Ordered Banner'}
                        upsoldFrom={'Recent Search'}
                        upsoldId={null}
                    />
                </Tab>
            )}
            {orderHeader &&
                orderHeader.orderStatus === 'OPEN' &&
                upsells.map(offer => (
                    <Tab
                        eventKey={'upsell' + offer.id}
                        key={offer.id}
                        title={
                            <span>
                                {offer.newUpsell && <span className={'new-tab-title'}>New</span>}
                                {offer.name}
                            </span>
                        }
                        tabClassName={'upsell-tab ' + (offer.newUpsell && 'new-tab')}
                    >
                        <OrderLines
                            products={offer.items}
                            loading={false}
                            imageUrl={offer.imageUrl}
                            imageAlt={offer.name + ' Banner'}
                            upsoldFrom={'Upsell offer'}
                            upsoldId={offer.id}
                        />
                    </Tab>
                ))}
        </Tabs>
    );
}
